import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { $MAX_WIDTH } from '../Consts'
import hero from '../assets/hero.jpg'
import { getInitialLang } from '../Common'

import AppBuilderDebug1 from '../assets/platform_screenshots/app_builder_debug1.jpg'
import AppBuilderEdit1 from '../assets/platform_screenshots/app_builder_edit1.jpg'
import AppBuilderOverview from '../assets/platform_screenshots/app_builder_overview.jpg'
import AppFileAssociations from '../assets/platform_screenshots/app_file_associations.jpg'
import AuditLogs from '../assets/platform_screenshots/audit_logs.jpg'
import PromptExample from '../assets/platform_screenshots/prompt_example.jpg'


import ManagePlugins from '../assets/platform_screenshots/manage_plugins.jpg'

import BackendTaskDesigner from '../assets/platform_screenshots/backend_task_designer.jpg'
import CafeShopWorkspace from '../assets/platform_screenshots/cafe_shop_workspace.jpg'
import InstantSearch from '../assets/platform_screenshots/instant_search.jpg'
import RbacPermissionsEditor from '../assets/platform_screenshots/rbac_permissions_editor.jpg'
import SecurityTab from '../assets/platform_screenshots/security_tab.jpg'
import SettingsFile from '../assets/platform_screenshots/settings_file.jpg'
import ZeroServers from '../assets/platform_screenshots/zero_servers.jpg'
import PluginCentric from '../assets/platform_screenshots/plugin_centric.jpg'
import AppBuilderRefineUIElem from '../assets/platform_screenshots/app_builder_refine_ui_elem.jpg'
import ObjectPermissions from '../assets/platform_screenshots/object_permissions.jpg'

import InstantHosting from '../assets/platform_screenshots/instant_hosting.jpg'
import ExplorerWorkspace from '../assets/platform_screenshots/explorer_workspace.jpg'


type Lang = 'en' | 'zh'

type IconKey =
  | 'apps'
  | 'ai'
  | 'tables'
  | 'screens'
  | 'plugins'
  | 'tasks'
  | 'debug'
  | 'workspace'
  | 'servers'

type ImageAsset = string | { src: string }

type Copy = {
  h1: string
  p: string
  primary: string
  imageAlt: string
  visualSubtitle: string
  heroHighlight: string
  heroBullets: string[]
  heroConclusion: string
  promptLabel: string
  promptExample: string
  promptNodes: Array<{ icon: IconKey; label: string; title: string; text: string }>
  builderEyebrow: string
  builderTitle: string
  stats: Array<{ value: string; label: string }>
  pluginEyebrow: string
  pluginTitle: string
  pluginSteps: Array<{ text: string; subitems?: string[] }>
  plugins: string[]
  pluginLibraryTitle: string
  pluginLibraryText: string
  pluginLibraryBullets: string[]
  capabilitiesEyebrow: string
  capabilitiesTitle: string
  businessAppsTitle: string
  capabilitiesText: string
  coreConcepts: Array<{ icon: IconKey; title: string; text: string }>
  coreConceptsNote: string
  featuresEyebrow: string
  featuresTitle: string
  featuresText: string
  features: Array<{ icon: IconKey; image: ImageAsset; title: string; text: string }>
  workflowEyebrow: string
  workflowTitle: string
  workflowText: string
  steps: Array<{ icon: IconKey; title: string; text: string }>
  demoEyebrow: string
  actionTitle: string
  actionImageAlt: string
  previousImageAria: string
  nextImageAria: string
  showImageAria: string
  featureIllustrationAlt: string
  demoTitle: string
  demoText: string
  demoButton: string
}

const EN: Copy = {
  h1: 'Properly Build Business Apps with AI',
  p: 'Vibe coding is unscalable. Agentic programming is imprecise.',
  primary: 'Book a Demo',
  imageAlt: 'BusinessA app builder illustration',
  visualSubtitle:
    'Example of a request that connects Tables, Screens, Backend Tasks and Plugins together.',
  heroHighlight: 'BusinessA gives you:',
  heroBullets: [
    'Maximal control over the App build',
    'Pixel-perfect Screens',
    'Functionality that exactly matches requirements',
  ],
  heroConclusion: 'All on one unified, enterprise, AI platform',
  promptLabel: 'Prompt',
  promptExample:
    'When Checkout successfully completes, insert an "Order" record into the "Orders" Table, send a Telegram message using the Telegram Plugin to the customer\'s phone, and finally navigate to the "Success Screen".',
  promptNodes: [
    {
      icon: 'screens',
      label: 'Screen',
      title: 'Success Screen',
      text: 'Navigate customers to the checkout success page after payment completes.',
    },
    {
      icon: 'tables',
      label: 'Table',
      title: 'Orders Table',
      text: 'Insert the completed Order record with customer, items, status and totals.',
    },
    {
      icon: 'plugins',
      label: 'Plugin',
      title: 'Telegram Plugin',
      text: 'Send the order summary to the customer’s number using Telegram.',
    },
  ],
  builderEyebrow: 'Example',
  builderTitle: 'Build, refine and debug with precision',
  stats: [
    { value: 'Tables First', label: 'model business data simply, with SQL-grade power underneath' },
    { value: 'Crosshair Edits', label: 'select the exact element to refine, fix or extend' },
    { value: 'One Platform', label: 'database, backend, files, hosting and AI in one system' },
  ],
  pluginEyebrow: 'AI App Builder',
  pluginTitle: 'Connect Tables, Screens and Plugins',
  pluginSteps: [
    {
      text: 'You describe the functionality you want',
    },
    {
      text: 'BusinessA connects the pieces and figures out the rest',
      subitems: [
        'what to insert to which Table',
        'which Screen to navigate to',
        'what Plugin to call',
      ],
    },
  ],
  plugins: [
    'User Auth',
    'Whatsapp',
    'Email',
    'Stripe payments',
    'Databases',
    'External APIs',
    'File uploads',
    'Analytics',
  ],
  pluginLibraryTitle: 'Everything else is a Plugin',
  pluginLibraryText:
    'Need to add auth, payments, messages, approvals, analytics or file handling to your App?',
  pluginLibraryBullets: [
    'Use one of our out-of-the-box Plugins or create your own!',
    'On BusinessA, complex integrations are squashed into simple, reusuable Plugins!',
  ],
  capabilitiesEyebrow: 'Why BusinessA',
  capabilitiesTitle: 'We simplified Apps into Screens and Tables',
  businessAppsTitle: 'BusinessA Apps',
  capabilitiesText:
    'With over a century of software engineering experience, we are confident that 99% of Apps can be deconstructed into Screens, Tables and Plugins',
  coreConcepts: [
    {
      icon: 'apps',
      title: 'Apps',
      text: 'An Application that users can launch - contains Screens, Tables, Settings & other files',
    },
    {
      icon: 'screens',
      title: 'Screens',
      text: 'What users of your App interact with - can be webpages, forms, dashboards, editors, etc.',
    },
    {
      icon: 'tables',
      title: 'Tables',
      text: 'Tables store business data such customers, orders, tickets, etc.',
    },
  ],
  coreConceptsNote:
    'Simply build Apps by creating Tables & Screens and connecting them together',
  featuresEyebrow: 'Platform features',
  featuresTitle: 'Everything else is built on this foundation',
  featuresText:
    'On top of Tables, Screens and Plugins, BusinessA offers enterprise features such as governance, collaboration, infrastructure, hosting, plugin workflows and auditability.',
  features: [
    {
      icon: 'workspace',
      image: RbacPermissionsEditor,
      title: 'End-to-end RBAC Governance',
      text: 'Control who can view, edit, share or operate Apps, Screens, Tables and files, with permissions that can reach from Apps & files down to table columns.',
    },
    {
      icon: 'apps',
      image: ExplorerWorkspace,
      title: 'Collaborative, Unified Workspace',
      text: 'Everything you ever need lives in one Windows Explorer-like workspace: Apps, Tables, Screens and custom files can be copied, moved, pasted, shared, exported and imported.',
    },
    {
      icon: 'servers',
      image: ZeroServers,
      title: 'Zero Infrastructure',
      text: 'BusinessA provides the backend servers, database and hosting, so your team can focus on building',
    },
    {
      icon: 'screens',
      image: InstantHosting,
      title: 'Instant Hosting',
      text: 'Anything built on BusinessA is instantly hosted and accessible from anywhere with an internet connection, without needing to install anything',
    },
    {
      icon: 'plugins',
      image: PluginCentric,
      title: 'Plugin-centric',
      text: 'Plugins are the bread and butter of BusinessA - sharable across projects, teams or even companies.',
    },
    {
      icon: 'debug',
      image: AuditLogs,
      title: 'Auditability',
      text: 'Everything done on the platform can be inspected through Audit Logs, giving teams a clear record of changes, actions and operational activity.',
    },
  ],
  workflowEyebrow: 'Example app',
  workflowTitle: 'Example: launch a menu ordering app',
  workflowText:
    'A restaurant, café or office cafeteria needs one app where staff manage menu items, customers place orders, and the team receives checkout notifications.',
  steps: [
    {
      icon: 'tables',
      title: '1. Define Menu and Order Tables',
      text: 'Create a Menu table for items, prices, categories and availability, plus an Orders table for customer details, order lines, status and totals.',
    },
    {
      icon: 'plugins',
      title: '2. Import the Telegram Sender Plugin',
      text: 'Add Telegram Sender so every new checkout can notify the kitchen or operations chat without writing integration code.',
    },
    {
      icon: 'screens',
      title: '3. Build Menu Editor and Order Screens',
      text: 'Ask AI to create a Menu Editor for staff and an ordering Screen for customers, binding each list, form and button to the right Tables.',
    },
    {
      icon: 'tasks',
      title: '4. Add checkout Tasks',
      text: 'Create a checkout Task that validates availability, stores the order, calculates totals and sends a Telegram message with the order summary.',
    },
    {
      icon: 'apps',
      title: '5. Publish for staff and customers',
      text: 'Publish the app, test the full ordering flow, then keep refining exact Screens, Tables and Tasks as the business process changes.',
    },
  ],
  demoEyebrow: 'Book a Demo',
  actionTitle: 'Platform in Action',
  actionImageAlt: 'Platform in Action',
  previousImageAria: 'Previous image',
  nextImageAria: 'Next image',
  showImageAria: 'Show image',
  featureIllustrationAlt: 'illustration',
  demoTitle: 'Build your next business app with BusinessA',
  demoText:
    'Show us your process, data and screens. We’ll show how BusinessA turns them into a controlled, full-stack application your team can operate.',
  demoButton: 'Book a Demo',
}

const ZH: Copy = {
  h1: '用 AI 正确构建业务应用',
  p: 'Vibe coding 无法规模化。Agentic programming 不够精确。',
  primary: '预约演示',
  imageAlt: 'BusinessA 应用构建器插图',
  visualSubtitle:
    '一个把数据表、页面、后端任务和插件连接在一起的请求示例。',
  heroHighlight: 'BusinessA 提供：',
  heroBullets: [
    '对 App 构建过程的最大控制',
    '像素级精准的 Screens',
    '与需求完全匹配的功能',
  ],
  heroConclusion: '全部运行在统一的企业级 AI 平台上',
  promptLabel: '提示词',
  promptExample:
    '当 Checkout 成功完成后，向 "Orders" 数据表插入一条 "Order" 记录，使用 Telegram 插件向客户的手机发送 Telegram 消息，最后跳转到 "Success Screen"。',
  promptNodes: [
    {
      icon: 'screens',
      label: '页面',
      title: 'Success Screen',
      text: '支付完成后，把客户带到成功确认页面。',
    },
    {
      icon: 'tables',
      label: '数据表',
      title: 'Orders 数据表',
      text: '写入已完成的 Order 记录，包括客户、商品、状态和总价。',
    },
    {
      icon: 'plugins',
      label: '插件',
      title: 'Telegram 插件',
      text: '使用 Telegram 向客户号码发送订单摘要。',
    },
  ],
  builderEyebrow: '示例',
  builderTitle: '构建、优化和调试都保持精准',
  stats: [
    { value: 'Tables First', label: '用简单方式建模业务数据，底层具备 SQL 级能力' },
    { value: '准星式编辑', label: '精确选择要优化、修复或扩展的元素' },
    { value: '统一平台', label: '数据库、后端、文件、托管和 AI 集成在同一个系统中' },
  ],
  pluginEyebrow: 'AI App Builder',
  pluginTitle: '连接数据表、页面和插件',
  pluginSteps: [
    {
      text: '你描述想要的功能',
    },
    {
      text: 'BusinessA 会连接各个部分，并推导其余细节',
      subitems: [
        '要向哪个数据表插入什么',
        '要调用哪个插件',
        '要导航到哪个页面',
      ],
    },
  ],
  plugins: [
    '用户认证',
    'Whatsapp',
    'Email',
    'Stripe 支付',
    '数据库',
    '外部 API',
    '文件上传',
    '分析',
  ],
  pluginLibraryTitle: '其他能力都是插件',
  pluginLibraryText:
    '需要为你的 App 添加认证、支付、消息、审批、分析或文件处理吗？',
  pluginLibraryBullets: [
    '使用我们的开箱即用插件，或创建你自己的插件！',
    '在 BusinessA 上，复杂集成会被压缩成简单、可复用的插件！',
  ],
  capabilitiesEyebrow: '为什么选择 BusinessA',
  capabilitiesTitle: '我们把应用简化为页面和数据表',
  businessAppsTitle: 'BusinessA 应用',
  capabilitiesText:
    '凭借超过一个世纪的软件工程经验，我们确信 99% 的 App 都可以拆解为页面、数据表和插件。',
  coreConcepts: [
    {
      icon: 'apps',
      title: '应用',
      text: '用户可以启动的应用程序——包含页面、数据表、设置和其他文件。',
    },
    {
      icon: 'screens',
      title: '页面',
      text: '你的 App 用户交互的内容——可以是网页、表单、仪表盘、编辑器等。',
    },
    {
      icon: 'tables',
      title: '数据表',
      text: '数据表存储客户、订单、工单等业务数据。',
    },
  ],
  coreConceptsNote:
    '在 BusinessA 上，你只需创建数据表、页面和插件，并用我们的 AI App Builder 把它们连接起来，就可以构建 Apps。',
  featuresEyebrow: '平台能力',
  featuresTitle: '所有能力都围绕这个基础展开',
  featuresText:
    '在数据表、页面和插件之上，BusinessA 加入治理、协作、基础设施、托管、插件工作流和审计能力，同时不会把你的应用变成黑盒。',
  features: [
    {
      icon: 'workspace',
      image: RbacPermissionsEditor,
      title: '端到端 RBAC 治理',
      text: '控制谁可以查看、编辑、分享或运行应用、页面、数据表和文件，权限可以从工作区一直细化到数据表列。',
    },
    {
      icon: 'apps',
      image: ExplorerWorkspace,
      title: '协作式统一工作区',
      text: '所有内容都位于一个类似 Windows Explorer 的工作区：应用、数据表、页面和自定义文件都可以复制、移动、粘贴、分享、导出和导入。',
    },
    {
      icon: 'servers',
      image: ZeroServers,
      title: '零基础设施',
      text: 'BusinessA 提供后端服务器、数据库和托管，因此你的团队可以专注于构建。',
    },
    {
      icon: 'screens',
      image: InstantHosting,
      title: '即时托管',
      text: '在 BusinessA 上构建的任何内容都会被即时托管，只要有互联网连接，就可以从任何地方访问，无需安装任何东西。',
    },
    {
      icon: 'plugins',
      image: PluginCentric,
      title: '以插件为中心',
      text: '插件是 BusinessA 的核心组成部分。它们是一等公民，插件市场也即将上线。',
    },
    {
      icon: 'debug',
      image: AuditLogs,
      title: '可审计性',
      text: '平台上的所有操作都可以通过 Audit Logs 检查，让团队清楚了解变更、动作和运行活动的完整记录。',
    },
  ],
  workflowEyebrow: '示例应用',
  workflowTitle: '示例：上线一个菜单点单应用',
  workflowText:
    '餐厅、咖啡店或公司食堂需要一个应用：员工维护菜单，客户提交订单，结账后团队收到通知。',
  steps: [
    {
      icon: 'tables',
      title: '1. 定义 Menu 和 Order 数据表',
      text: '创建 Menu 表保存菜品、价格、分类和库存状态，再创建 Orders 表保存客户信息、订单明细、状态和总价。',
    },
    {
      icon: 'plugins',
      title: '2. 导入 Telegram Sender 插件',
      text: '添加 Telegram Sender，让每次结账后都能自动把订单通知发送到厨房或运营群聊，无需手写集成代码。',
    },
    {
      icon: 'screens',
      title: '3. 构建菜单编辑器和点单页面',
      text: '让 AI 创建员工使用的菜单编辑页面，以及客户使用的点单页面，并把列表、表单和按钮绑定到正确的数据表。',
    },
    {
      icon: 'tasks',
      title: '4. 添加结账任务',
      text: '创建 checkout Task，校验可用性、保存订单、计算总价，并通过 Telegram 发送订单摘要。',
    },
    {
      icon: 'apps',
      title: '5. 发布给员工和客户使用',
      text: '发布应用，测试完整点单流程，然后随着业务变化继续精准优化页面、数据表和 Tasks。',
    },
  ],
  demoEyebrow: '预约演示',
  actionTitle: '平台实战',
  actionImageAlt: '平台实战',
  previousImageAria: '上一张图片',
  nextImageAria: '下一张图片',
  showImageAria: '显示图片',
  featureIllustrationAlt: '插图',
  demoTitle: '用 BusinessA 构建你的下一个业务应用',
  demoText:
    '向我们展示你的流程、数据和页面。我们会展示 BusinessA 如何把它们变成团队可运营的可控全栈应用。',
  demoButton: '预约演示',
}

const LANGS: Record<Lang, Copy> = { en: EN, zh: ZH }

const svgDataUri = (svg: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`

const createVisual = (accent: string, accentSoft: string, variant: number) => {
  const layouts = [
    `
      <rect x="170" y="82" width="620" height="118" rx="28" fill="#ffffff" opacity=".16"/>
      <rect x="214" y="123" width="390" height="14" rx="7" fill="#ffffff" opacity=".5"/>
      <rect x="214" y="154" width="510" height="12" rx="6" fill="#ffffff" opacity=".27"/>
      <path d="M480 218C480 282 246 280 246 354" fill="none" stroke="${accent}" stroke-opacity=".9" stroke-width="8" stroke-linecap="round" stroke-dasharray="2 22"/>
      <path d="M480 218C480 286 480 302 480 354" fill="none" stroke="${accent}" stroke-opacity=".9" stroke-width="8" stroke-linecap="round" stroke-dasharray="2 22"/>
      <path d="M480 218C480 282 714 280 714 354" fill="none" stroke="${accent}" stroke-opacity=".9" stroke-width="8" stroke-linecap="round" stroke-dasharray="2 22"/>
      <rect x="92" y="354" width="260" height="150" rx="28" fill="#ffffff" opacity=".13" stroke="#ffffff" stroke-opacity=".22"/>
      <rect x="390" y="354" width="180" height="150" rx="28" fill="#ffffff" opacity=".13" stroke="#ffffff" stroke-opacity=".22"/>
      <rect x="608" y="354" width="260" height="150" rx="28" fill="#ffffff" opacity=".13" stroke="#ffffff" stroke-opacity=".22"/>
      <circle cx="144" cy="408" r="24" fill="${accent}" opacity=".42"/>
      <circle cx="444" cy="408" r="24" fill="${accent}" opacity=".42"/>
      <circle cx="660" cy="408" r="24" fill="${accent}" opacity=".42"/>
      <rect x="184" y="392" width="120" height="12" rx="6" fill="#ffffff" opacity=".5"/>
      <rect x="184" y="424" width="88" height="10" rx="5" fill="#ffffff" opacity=".28"/>
      <rect x="480" y="392" width="54" height="12" rx="6" fill="#ffffff" opacity=".5"/>
      <rect x="480" y="424" width="44" height="10" rx="5" fill="#ffffff" opacity=".28"/>
      <rect x="700" y="392" width="118" height="12" rx="6" fill="#ffffff" opacity=".5"/>
      <rect x="700" y="424" width="86" height="10" rx="5" fill="#ffffff" opacity=".28"/>
    `,
    `
      <rect x="82" y="78" width="516" height="398" rx="34" fill="#ffffff" opacity=".14" stroke="#ffffff" stroke-opacity=".2"/>
      <rect x="116" y="120" width="110" height="306" rx="22" fill="#ffffff" opacity=".12"/>
      <rect x="258" y="120" width="292" height="62" rx="20" fill="#ffffff" opacity=".16"/>
      <rect x="258" y="210" width="128" height="158" rx="22" fill="#ffffff" opacity=".13"/>
      <rect x="416" y="210" width="134" height="158" rx="22" fill="#ffffff" opacity=".13"/>
      <rect x="136" y="148" width="62" height="10" rx="5" fill="#ffffff" opacity=".45"/>
      <rect x="136" y="188" width="46" height="10" rx="5" fill="#ffffff" opacity=".25"/>
      <rect x="136" y="228" width="58" height="10" rx="5" fill="#ffffff" opacity=".25"/>
      <rect x="290" y="144" width="182" height="12" rx="6" fill="#ffffff" opacity=".45"/>
      <rect x="288" y="258" width="68" height="68" rx="18" fill="${accent}" opacity=".34"/>
      <rect x="448" y="258" width="68" height="68" rx="18" fill="${accent}" opacity=".34"/>
      <rect x="642" y="132" width="236" height="84" rx="26" fill="${accentSoft}" opacity=".22" stroke="#ffffff" stroke-opacity=".2"/>
      <rect x="642" y="256" width="236" height="84" rx="26" fill="${accentSoft}" opacity=".18" stroke="#ffffff" stroke-opacity=".18"/>
      <rect x="642" y="380" width="236" height="84" rx="26" fill="${accentSoft}" opacity=".2" stroke="#ffffff" stroke-opacity=".2"/>
      <path d="M598 170H642" stroke="${accent}" stroke-opacity=".8" stroke-width="8" stroke-linecap="round"/>
      <path d="M598 298H642" stroke="${accent}" stroke-opacity=".8" stroke-width="8" stroke-linecap="round"/>
      <path d="M598 422H642" stroke="${accent}" stroke-opacity=".8" stroke-width="8" stroke-linecap="round"/>
      <rect x="684" y="162" width="124" height="12" rx="6" fill="#ffffff" opacity=".48"/>
      <rect x="684" y="286" width="124" height="12" rx="6" fill="#ffffff" opacity=".48"/>
      <rect x="684" y="410" width="124" height="12" rx="6" fill="#ffffff" opacity=".48"/>
    `,
    `
      <rect x="96" y="86" width="768" height="430" rx="38" fill="#ffffff" opacity=".13" stroke="#ffffff" stroke-opacity=".22"/>
      <rect x="142" y="134" width="470" height="300" rx="30" fill="#ffffff" opacity=".12"/>
      <rect x="178" y="172" width="220" height="16" rx="8" fill="#ffffff" opacity=".5"/>
      <rect x="178" y="218" width="356" height="12" rx="6" fill="#ffffff" opacity=".25"/>
      <rect x="178" y="254" width="316" height="12" rx="6" fill="#ffffff" opacity=".25"/>
      <rect x="178" y="310" width="142" height="58" rx="18" fill="${accent}" opacity=".4"/>
      <rect x="352" y="310" width="142" height="58" rx="18" fill="#ffffff" opacity=".14"/>
      <rect x="640" y="150" width="166" height="110" rx="28" fill="${accentSoft}" opacity=".2" stroke="#ffffff" stroke-opacity=".2"/>
      <rect x="640" y="304" width="166" height="110" rx="28" fill="${accentSoft}" opacity=".18" stroke="#ffffff" stroke-opacity=".18"/>
      <circle cx="416" cy="340" r="106" fill="none" stroke="${accent}" stroke-opacity=".42" stroke-width="4"/>
      <circle cx="416" cy="340" r="54" fill="none" stroke="${accent}" stroke-opacity=".7" stroke-width="5"/>
      <path d="M416 216V464M292 340H540" stroke="${accent}" stroke-opacity=".72" stroke-width="5" stroke-linecap="round"/>
      <rect x="674" y="186" width="82" height="12" rx="6" fill="#ffffff" opacity=".46"/>
      <rect x="674" y="340" width="82" height="12" rx="6" fill="#ffffff" opacity=".46"/>
      <path d="M612 206C658 206 640 206 640 206" stroke="${accent}" stroke-opacity=".8" stroke-width="7" stroke-linecap="round"/>
      <path d="M612 360C658 360 640 360 640 360" stroke="${accent}" stroke-opacity=".8" stroke-width="7" stroke-linecap="round"/>
    `,
  ]

  return svgDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 960 620">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${accent}" stop-opacity=".42"/>
          <stop offset=".52" stop-color="${accentSoft}" stop-opacity=".36"/>
          <stop offset="1" stop-color="#123f96" stop-opacity=".9"/>
        </linearGradient>
        <radialGradient id="glow" cx=".72" cy=".08" r=".7">
          <stop offset="0" stop-color="#ffffff" stop-opacity=".38"/>
          <stop offset=".58" stop-color="${accent}" stop-opacity=".14"/>
          <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="960" height="620" rx="56" fill="url(#bg)"/>
      <rect width="960" height="620" rx="56" fill="url(#glow)"/>
      <circle cx="84" cy="92" r="148" fill="#ffffff" opacity=".08"/>
      <circle cx="872" cy="518" r="190" fill="${accent}" opacity=".12"/>
      <rect x="44" y="42" width="872" height="536" rx="42" fill="#ffffff" opacity=".08" stroke="#ffffff" stroke-opacity=".16"/>
      ${layouts[variant % layouts.length]}
    </svg>
  `)
}

type SlideImage = ImageAsset
type SlideCaption = string | Partial<Record<Lang, string>>

type ScreenBuilderSlide = {
  /**
   * Supports generated data URIs and imported image assets.
   *
   * Example:
   *   import mySlide from '../assets/my-slide.png'
   *   { image: mySlide, caption: 'My caption' }
   */
  image: SlideImage
  caption?: SlideCaption | null
}

const getSlideImageSrc = (image: SlideImage) =>
  typeof image === 'string' ? image : image.src

const getSlideCaption = (
  caption: ScreenBuilderSlide['caption'],
  lang: Lang
) => {
  if (!caption) return null
  if (typeof caption === 'string') return caption
  return caption[lang] ?? caption.en ?? null
}

const SCREEN_BUILDER_SLIDES: ScreenBuilderSlide[] = [
  {
    image: CafeShopWorkspace,
    caption: {
      en: 'Collaborative, unified, Windows-explorer-like workspace',
      zh: '协作式、统一的、类似 Windows Explorer 的工作区',
    },
  },
  {
    image: InstantSearch,
    caption: {
      en: 'Use instant search to find apps, screens, tables and files quickly.',
      zh: '使用即时搜索快速找到应用、页面、数据表和文件。',
    },
  },
  {
    image: AppBuilderOverview,
    caption: {
      en: 'An App containing Screens, Tables and Plugins',
      zh: '一个包含页面、数据表和插件的 App',
    },
  },
  {
    image: PromptExample,
    caption: {
      en: 'Precisely refine functionality on UI elements',
      zh: '选择具体 UI 元素，通过可控的 AI 编辑进行优化。',
    },
  },
  {
    image: AppBuilderDebug1,
    caption: {
      en: 'Precisely debug issues with UI elements',
      zh: '选择具体 UI 元素，通过可控的 AI 编辑进行优化。',
    },
  },
  {
    image: ManagePlugins,
    caption: {
      en: 'Import plugins with a few clicks',
      zh: '选择具体 UI 元素，通过可控的 AI 编辑进行优化。',
    },
  },
  {
    image: BackendTaskDesigner,
    caption: {
      en: 'Design Backend Tasks with the drag and drop flowchart editor',
      zh: '使用拖拽式流程图编辑器设计 Backend Tasks',
    },
  },
  {
    image: ObjectPermissions,
    caption: {
      en: 'Granular object permissions from Apps all the way down to Table Columns!',
      zh: '从 Apps 一直到 Table Columns 的细粒度对象权限！',
    },
  },
  {
    image: RbacPermissionsEditor,
    caption: {
      en: 'Granular role-based user permissions',
      zh: '细粒度的基于角色的用户权限',
    },
  },
]

const carouselImagePreloadCache = new Map<string, Promise<void>>()

const preloadCarouselImage = (src: string) => {
  if (typeof window === 'undefined') return Promise.resolve()

  const cached = carouselImagePreloadCache.get(src)
  if (cached) return cached

  const promise = new Promise<void>((resolve) => {
    const image = new Image()
    const settle = () => {
      if (typeof image.decode === 'function') {
        image
          .decode()
          .catch(() => undefined)
          .then(() => resolve())
      } else {
        resolve()
      }
    }

    image.onload = settle
    image.onerror = () => resolve()
    image.src = src
    if (image.complete) settle()
  })

  carouselImagePreloadCache.set(src, promise)
  return promise
}

const FeatureIcon: React.FC<{
  name: IconKey
  size?: number
  style?: React.CSSProperties
}> = ({ name, size = 24, style }) => {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style,
  }

  if (name === 'apps') {
    return (
      <svg {...props}>
        <rect x="3.5" y="4" width="17" height="16" rx="3" />
        <path d="M7.5 8h3.5" />
        <path d="M7.5 12h9" />
        <path d="M7.5 16h5.5" />
        <path d="M15.5 7.5l1.1 1.1 2-2" />
      </svg>
    )
  }

  if (name === 'ai') {
    return (
      <svg {...props}>
        <path d="M12 3.5l1.1 3.3 3.4 1.2-3.4 1.2-1.1 3.3-1.1-3.3L7.5 8l3.4-1.2L12 3.5Z" />
        <path d="M18 13.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z" />
        <path d="M6.2 13.8l.6 1.7 1.7.6-1.7.6-.6 1.7-.6-1.7-1.7-.6 1.7-.6.6-1.7Z" />
      </svg>
    )
  }

  if (name === 'tables') {
    return (
      <svg {...props}>
        <path d="M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3Z" />
        <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
        <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
      </svg>
    )
  }

  if (name === 'screens') {
    return (
      <svg {...props}>
        <rect x="3" y="4" width="18" height="13" rx="2.5" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
        <path d="M15.5 7.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z" />
      </svg>
    )
  }

  if (name === 'plugins') {
    return (
      <svg {...props}>
        <path d="M8 4v4" />
        <path d="M16 4v4" />
        <path d="M6.5 8h11v3.5a5.5 5.5 0 0 1-11 0V8Z" />
        <path d="M12 17v4" />
        <path d="M9.5 21h5" />
      </svg>
    )
  }

  if (name === 'tasks') {
    return (
      <svg {...props}>
        <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11Z" />
        <path d="M8 8h5" />
        <path d="M8 12h8" />
        <path d="M8 16h3" />
        <path d="M15 15l2 2 3-4" />
      </svg>
    )
  }

  if (name === 'debug') {
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="6.5" />
        <path d="M12 2.5v3" />
        <path d="M12 18.5v3" />
        <path d="M2.5 12h3" />
        <path d="M18.5 12h3" />
        <circle cx="12" cy="12" r="1.5" />
      </svg>
    )
  }

  if (name === 'servers') {
    return (
      <svg {...props}>
        <rect x="4" y="4" width="16" height="5.5" rx="2" />
        <rect x="4" y="14.5" width="16" height="5.5" rx="2" />
        <path d="M7.5 6.75h.01" />
        <path d="M10.5 6.75h5.5" />
        <path d="M7.5 17.25h.01" />
        <path d="M10.5 17.25h5.5" />
        <path d="M12 9.5v5" />
        <path d="M8 12h8" />
      </svg>
    )
  }

  return (
    <svg {...props}>
      <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h4l2 2h6A2.5 2.5 0 0 1 20.5 9.5v7A2.5 2.5 0 0 1 18 19H6a2.5 2.5 0 0 1-2.5-2.5v-9Z" />
      <path d="M8 13l2.2 2.2L16 9.8" />
    </svg>
  )
}

const useDesktop = () => {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width:1024px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(min-width:1024px)')
    const update = () => setDesktop(media.matches)

    update()

    if (media.addEventListener) {
      media.addEventListener('change', update)
      return () => media.removeEventListener('change', update)
    }

    media.addListener(update)
    return () => media.removeListener(update)
  }, [])

  return desktop
}

const FeatherUnderline: React.FC<{
  align?: 'left' | 'center'
  width?: number
}> = ({ align = 'left', width = 190 }) => (
  <div
    aria-hidden="true"
    style={{
      width,
      maxWidth: '62%',
      height: 10,
      marginTop: '.72rem',
      marginLeft: align === 'center' ? 'auto' : 0,
      marginRight: align === 'center' ? 'auto' : 0,
      position: 'relative',
      pointerEvents: 'none',
    }}
  >
    <span
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 2,
        height: 3,
        borderRadius: 999,
        background:
          'linear-gradient(90deg,rgba(217,248,255,0),rgba(217,248,255,.62) 14%,rgba(255,255,255,.92) 50%,rgba(217,248,255,.52) 86%,rgba(217,248,255,0))',
        boxShadow: '0 0 14px rgba(217,248,255,.28)',
      }}
    />
    <span
      style={{
        position: 'absolute',
        left: align === 'center' ? '50%' : '12%',
        bottom: 0,
        width: '42%',
        height: 2,
        transform: align === 'center' ? 'translateX(-50%)' : 'none',
        borderRadius: 999,
        background:
          'linear-gradient(90deg,rgba(255,255,255,.72),rgba(217,248,255,.18))',
        opacity: 0.72,
      }}
    />
  </div>
)

const HomePageClient: React.FC = () => {
  const nav = useNavigate()
  const desktop = useDesktop()
  const [lang] = useState<Lang>(() => {
    const initial = getInitialLang()
    return initial === 'zh' ? 'zh' : 'en'
  })
  const [activeSlide, setActiveSlide] = useState(0)
  const [carouselPosition, setCarouselPosition] = useState(1)
  const [carouselTransition, setCarouselTransition] = useState(true)
  const [primaryHover, setPrimaryHover] = useState(false)
  const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null)
  const slideRequestRef = useRef(0)
  const slideAnimatingRef = useRef(false)
  const carouselPositionRef = useRef(1)
  const slideTransitionFallbackRef = useRef<number | null>(null)
  const slideTransitionRestoreRef = useRef<number | null>(null)

  useEffect(() => {
    if (!modalImage || typeof window === 'undefined') return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setModalImage(null)
    }

    const previousOverflow = document.body.style.overflow
    const previousDocumentOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.documentElement.style.overflow = previousDocumentOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [modalImage])

  useEffect(() => {
    SCREEN_BUILDER_SLIDES.forEach((slide) => {
      preloadCarouselImage(getSlideImageSrc(slide.image))
    })

    return () => {
      slideRequestRef.current += 1
      slideAnimatingRef.current = false

      if (typeof window !== 'undefined') {
        if (slideTransitionFallbackRef.current !== null) {
          window.clearTimeout(slideTransitionFallbackRef.current)
        }

        if (slideTransitionRestoreRef.current !== null) {
          window.cancelAnimationFrame(slideTransitionRestoreRef.current)
        }
      }
    }
  }, [])

  useEffect(() => {
    carouselPositionRef.current = carouselPosition
  }, [carouselPosition])

  const t = LANGS[lang]
  const coreConceptCards = t.coreConcepts.filter((card) => card.icon !== 'apps')
  const featureCards = t.features
  const activeActionSlide = SCREEN_BUILDER_SLIDES[activeSlide]
  const activeActionCaption = getSlideCaption(activeActionSlide.caption, lang)

  const carouselTrackSlides = [
    {
      slide: SCREEN_BUILDER_SLIDES[SCREEN_BUILDER_SLIDES.length - 1]!,
      slideIndex: SCREEN_BUILDER_SLIDES.length - 1,
    },
    ...SCREEN_BUILDER_SLIDES.map((slide, slideIndex) => ({ slide, slideIndex })),
    {
      slide: SCREEN_BUILDER_SLIDES[0]!,
      slideIndex: 0,
    },
  ]

  const clearSlideTransitionFallback = () => {
    if (
      typeof window !== 'undefined' &&
      slideTransitionFallbackRef.current !== null
    ) {
      window.clearTimeout(slideTransitionFallbackRef.current)
      slideTransitionFallbackRef.current = null
    }
  }

  const restoreCarouselTransition = () => {
    if (typeof window === 'undefined') {
      setCarouselTransition(true)
      slideAnimatingRef.current = false
      return
    }

    if (slideTransitionRestoreRef.current !== null) {
      window.cancelAnimationFrame(slideTransitionRestoreRef.current)
    }

    slideTransitionRestoreRef.current = window.requestAnimationFrame(() => {
      slideTransitionRestoreRef.current = window.requestAnimationFrame(() => {
        setCarouselTransition(true)
        slideAnimatingRef.current = false
        slideTransitionRestoreRef.current = null
      })
    })
  }

  const finishCarouselMove = () => {
    if (!slideAnimatingRef.current) return

    clearSlideTransitionFallback()

    const position = carouselPositionRef.current
    const lastRealPosition = SCREEN_BUILDER_SLIDES.length

    if (position === 0 || position === lastRealPosition + 1) {
      const normalizedPosition = position === 0 ? lastRealPosition : 1

      setCarouselTransition(false)
      carouselPositionRef.current = normalizedPosition
      setCarouselPosition(normalizedPosition)
      restoreCarouselTransition()
      return
    }

    slideAnimatingRef.current = false
  }

  const moveSlide = (direction: 1 | -1) => {
    if (slideAnimatingRef.current || SCREEN_BUILDER_SLIDES.length < 2) return

    const nextSlide =
      (activeSlide + direction + SCREEN_BUILDER_SLIDES.length) %
      SCREEN_BUILDER_SLIDES.length
    const request = ++slideRequestRef.current

    slideAnimatingRef.current = true

    preloadCarouselImage(getSlideImageSrc(SCREEN_BUILDER_SLIDES[nextSlide].image)).then(() => {
      if (slideRequestRef.current !== request) {
        slideAnimatingRef.current = false
        return
      }

      setActiveSlide(nextSlide)
      setCarouselTransition(true)

      const nextPosition = carouselPositionRef.current + direction
      carouselPositionRef.current = nextPosition
      setCarouselPosition(nextPosition)

      if (typeof window !== 'undefined') {
        const reducedMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches

        slideTransitionFallbackRef.current = window.setTimeout(
          finishCarouselMove,
          reducedMotion ? 32 : 460
        )
      }
    })
  }
  const goPrevSlide = () => moveSlide(-1)
  const goNextSlide = () => moveSlide(1)
  const openImageModal = (image: ImageAsset, alt: string) =>
    setModalImage({ src: getSlideImageSrc(image), alt })
  const getImageOpenHandlers = (image: ImageAsset, alt: string) => ({
    role: 'button' as const,
    tabIndex: 0,
    onClick: () => openImageModal(image, alt),
    onKeyDown: (event: React.KeyboardEvent<HTMLImageElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openImageModal(image, alt)
      }
    },
  })

  const grid =
    'repeating-linear-gradient(to right,rgba(255,255,255,.035) 0 1px,transparent 1px 92px),repeating-linear-gradient(to bottom,rgba(255,255,255,.035) 0 1px,transparent 1px 92px)'

  const surfaceBg =
    'linear-gradient(180deg,rgba(160,219,255,.105),rgba(33,104,198,.04))'
  const surfaceBgSoft =
    'linear-gradient(180deg,rgba(143,207,255,.085),rgba(26,94,190,.032))'
  const surfaceBgProminent =
    'linear-gradient(180deg,rgba(184,231,255,.13),rgba(36,111,202,.05))'
  const surfaceBorder = '1px solid rgba(224,247,255,.14)'
  const surfaceBorderSoft = '1px solid rgba(224,247,255,.12)'
  const surfaceShadow =
    '0 16px 44px rgba(13,61,134,.14), inset 0 1px 0 rgba(255,255,255,.045)'
  const surfaceShadowSoft =
    '0 10px 30px rgba(13,61,134,.1), inset 0 1px 0 rgba(255,255,255,.035)'
  const iconBg =
    'linear-gradient(135deg,rgba(190,231,255,.14),rgba(255,255,255,.045))'
  const iconBorder = '1px solid rgba(224,247,255,.13)'
  const labelBg = 'rgba(255,255,255,.065)'
  const labelBorder = '1px solid rgba(217,248,255,.14)'

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        background:
          'radial-gradient(circle at 78% 8%,rgba(190,231,255,.22),transparent 31%),radial-gradient(circle at 12% 24%,rgba(118,194,255,.16),transparent 36%),radial-gradient(circle at 50% 0%,rgba(255,255,255,.08),transparent 48%),linear-gradient(180deg,#2f88d6 0%,#2471c9 34%,#1b58b5 68%,#153f96 100%)',
        color: '#fff',
        fontFamily:
          'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      <style>
        {`
          .ba-hover-card {
            transition: transform .24s ease, filter .24s ease, border-color .24s ease, box-shadow .24s ease;
            will-change: transform;
          }
          .ba-hover-card img {
            transition: transform .28s ease, filter .28s ease;
          }
          @media (hover: hover) and (pointer: fine) {
            .ba-hover-card:hover {
              transform: translateY(-3px);
              filter: brightness(1.035);
              border-color: rgba(224,247,255,.24) !important;
              box-shadow: 0 22px 56px rgba(13,61,134,.18), inset 0 1px 0 rgba(255,255,255,.07) !important;
            }
            .ba-hover-card.ba-soft-hover:hover {
              transform: translateY(-2px);
            }
            .ba-hover-card:hover img {
              transform: scale(1.012);
              filter: saturate(1.08) brightness(1.03);
            }
          }
          .ba-carousel-track {
            will-change: transform;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            transform-style: preserve-3d;
          }
          .ba-carousel-slide,
          .ba-carousel-slide img {
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            contain: paint;
          }
          @media (prefers-reduced-motion: reduce) {
            .ba-hover-card, .ba-hover-card img { transition: none; }
            .ba-carousel-track {
              transition: none !important;
            }
          }
        `}
      </style>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: grid,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 620,
          height: 620,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(221,244,255,.18),rgba(93,185,255,0) 70%)',
          top: -230,
          right: -210,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 720,
          height: 720,
          borderRadius: '50%',
          background:
            'radial-gradient(circle,rgba(114,190,255,.16),rgba(62,133,255,0) 72%)',
          bottom: 300,
          left: -310,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg,rgba(255,255,255,.02) 0%,rgba(4,38,104,.14) 44%,rgba(2,18,61,.34) 100%)',
          pointerEvents: 'none',
        }}
      />

      <section
        style={{
          maxWidth: $MAX_WIDTH,
          width: '100%',
          margin: '0 auto',
          padding: desktop ? '9.25rem 2rem 6rem' : '7rem 1.15rem 4rem',
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: desktop
            ? 'minmax(0,.88fr) minmax(420px,.92fr)'
            : '1fr',
          alignItems: desktop ? 'center' : 'start',
          columnGap: desktop ? '2rem' : undefined,
          rowGap: desktop ? '2.35rem' : '2rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <h1
          style={{
            gridColumn: desktop ? '1 / -1' : undefined,
            fontSize: desktop ? '4.35rem' : '2.68rem',
            fontWeight: 850,
            lineHeight: 1.04,
            letterSpacing: '-.052em',
            margin: 0,
            maxWidth: desktop ? 980 : 760,
            textAlign: desktop ? 'left' : 'center',
            justifySelf: desktop ? 'start' : 'center',
            textShadow: '0 18px 42px rgba(6,47,110,.18)',
          }}
        >
          {t.h1}
        </h1>

        <div
          style={{
            textAlign: desktop ? 'left' : 'center',
            maxWidth: desktop ? 650 : 760,
            justifySelf: desktop ? 'start' : 'center',
            alignSelf: 'center',
          }}
        >
            <p
              style={{
                fontSize: desktop ? '1.18rem' : '1.04rem',
                opacity: 0.9,
                maxWidth: 650,
                margin: 0,
                lineHeight: 1.68,
                marginLeft: desktop ? 0 : 'auto',
                marginRight: desktop ? 0 : 'auto',
              }}
            >
              {t.p}
            </p>

            <div
              style={{
                marginTop: '.85rem',
                padding: desktop ? '.9rem 1rem' : '.82rem .9rem',
                borderRadius: 18,
                background:
                  'linear-gradient(135deg,rgba(255,255,255,.14),rgba(217,248,255,.08))',
                border: '1px solid rgba(224,247,255,.18)',
                boxShadow: '0 12px 28px rgba(13,61,134,.13)',
                color: '#fff',
              }}
            >
              <div
                style={{
                  fontWeight: 850,
                  fontSize: desktop ? '1.05rem' : '1rem',
                  lineHeight: 1.35,
                }}
              >
                {t.heroHighlight}
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  margin: '.7rem 0 0',
                  padding: 0,
                  display: 'grid',
                  gap: '.52rem',
                  textAlign: 'left',
                }}
              >
                {t.heroBullets.map((bullet) => (
                  <li
                    key={bullet}
                    style={{
                      display: 'flex',
                      gap: '.55rem',
                      alignItems: 'flex-start',
                      fontWeight: 750,
                      lineHeight: 1.45,
                    }}
                  >
                    <span aria-hidden="true">✓</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              style={{
                marginTop: '.85rem',
                color: 'rgba(255,255,255,.96)',
                fontWeight: 850,
                fontSize: desktop ? '1.08rem' : '1rem',
                lineHeight: 1.45,
                textAlign: desktop ? 'left' : 'center',
                textShadow: '0 10px 26px rgba(6,47,110,.16)',
              }}
            >
              {t.heroConclusion}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: desktop ? 'row' : 'column',
                gap: '1rem',
                marginTop: '2.1rem',
                justifyContent: desktop ? 'flex-start' : 'center',
                alignItems: 'center',
              }}
            >
              <button
                style={{
                  width: desktop ? 'auto' : 'min(100%, 360px)',
                  background: primaryHover
                    ? 'linear-gradient(90deg,#ffffff 0%,#eef7ff 100%)'
                    : 'linear-gradient(90deg,#ffffff 0%,#f7fbff 100%)',
                  color: '#08265c',
                  padding: '1.05rem 2.35rem',
                  fontSize: '1.04rem',
                  fontWeight: 750,
                  border: '1px solid rgba(255,255,255,.86)',
                  borderRadius: 16,
                  cursor: 'pointer',
                  boxShadow: primaryHover
                    ? '0 16px 38px rgba(21,83,163,.28)'
                    : '0 12px 30px rgba(21,83,163,.2)',
                  transition: 'box-shadow .25s, transform .25s, background .25s',
                  transform: primaryHover ? 'translateY(-2px)' : 'translateY(0)',
                }}
                onMouseEnter={() => setPrimaryHover(true)}
                onMouseLeave={() => setPrimaryHover(false)}
                onClick={() => nav('/book-demo')}
              >
                {t.primary}
              </button>
            </div>
        </div>

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            className="ba-hover-card ba-soft-hover"
            style={{
              width: '100%',
              maxWidth: desktop ? 680 : 560,
              margin: '0 auto',
              borderRadius: desktop ? 18 : 12,
              padding: desktop ? 0 : '.6rem',
              background:
                surfaceBgProminent,
              border: surfaceBorder,
              boxShadow:
                '0 22px 62px rgba(13,61,134,.16), inset 0 1px 0 rgba(255,255,255,.05)',
              overflow: 'hidden',
            }}
          >
            <img
              src={AppBuilderRefineUIElem}
              alt={t.imageAlt}
              {...getImageOpenHandlers(AppBuilderRefineUIElem, t.imageAlt)}
              style={{
                width: '100%',
                display: 'block',
                borderRadius: desktop ? 17 : 12,
                boxShadow: '0 18px 46px rgba(17,68,139,.24)',
                cursor: 'zoom-in',
              }}
            />
          </div>
        </div>
      </section>

      <section
        id="capabilities"
        style={{
          maxWidth: $MAX_WIDTH,
          width: '100%',
          margin: '0 auto',
          padding: desktop ? '2rem 2rem 6rem' : '2.75rem 1.15rem 4.5rem',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 840,
            marginBottom: '2.8rem',
            textAlign: desktop ? 'left' : 'center',
            marginLeft: desktop ? 0 : 'auto',
            marginRight: desktop ? 0 : 'auto',
          }}
        >
          <div
            style={{
              fontSize: '.82rem',
              textTransform: 'uppercase',
              letterSpacing: '.12em',
              opacity: 0.68,
              fontWeight: 850,
              marginBottom: '1rem',
              color: '#d9f8ff',
            }}
          >
            {t.capabilitiesEyebrow}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: desktop ? '3.05rem' : '2.05rem',
              letterSpacing: '-.045em',
              lineHeight: 1.1,
            }}
          >
            {t.capabilitiesTitle}
          </h2>
          <FeatherUnderline align={desktop ? 'left' : 'center'} width={220} />
          <p
            style={{
              margin: '1.2rem 0 0',
              fontSize: desktop ? '1.08rem' : '1rem',
              lineHeight: 1.65,
              opacity: 0.84,
              maxWidth: 760,
              marginLeft: desktop ? 0 : 'auto',
              marginRight: desktop ? 0 : 'auto',
            }}
          >
            {t.capabilitiesText}
          </p>
        </div>

        <div
          style={{
            borderRadius: 30,
            padding: 0,
            background: surfaceBgProminent,
            border: surfaceBorder,
            boxShadow: surfaceShadow,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              minHeight: desktop ? 46 : 42,
              padding: desktop ? '0 1.15rem' : '0 .9rem',
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateColumns: '72px minmax(0,1fr) 72px',
              alignItems: 'center',
              background:
                'linear-gradient(180deg,rgba(255,255,255,.18),rgba(255,255,255,.08))',
              borderBottom: '1px solid rgba(224,247,255,.15)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12)',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.45rem',
              }}
            >
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#ff5f57',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#ffbd2e',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#28c840',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
            </div>
            <div
              style={{
                minWidth: 0,
                textAlign: 'center',
                fontSize: desktop ? '.98rem' : '.9rem',
                fontWeight: 850,
                letterSpacing: '-.015em',
                color: 'rgba(255,255,255,.95)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: '0 2px 12px rgba(0,0,0,.16)',
              }}
            >
              {t.businessAppsTitle}
            </div>
            <div />
          </div>

          <div
            style={{
              padding: desktop ? '1.25rem' : '.85rem',
              boxSizing: 'border-box',
            }}
          >
          <div
          style={{
            display: 'grid',
            gridTemplateColumns: desktop ? 'repeat(2,minmax(0,1fr))' : '1fr',
            gap: desktop ? '1.15rem' : '1rem',
          }}
        >
          {coreConceptCards.map((card) => (
            <div
              className="ba-hover-card"
              key={card.title}
              style={{
                minHeight: desktop ? 120 : 'auto',
                padding: desktop ? '1.7rem' : '1.35rem',
                borderRadius: 22,
                background: surfaceBg,
                border: surfaceBorder,
                boxShadow: surfaceShadow,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1.1rem',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    flex: '0 0 44px',
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    background: iconBg,
                    border: iconBorder,
                    boxShadow: '0 8px 18px rgba(13,61,134,.1)',
                  }}
                >
                  <FeatureIcon name={card.icon} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: desktop ? '1.42rem' : '1.28rem',
                    letterSpacing: '-.02em',
                    lineHeight: 1.25,
                  }}
                >
                  {card.title}
                </h3>
              </div>
              <p
                style={{
                  margin: '.75rem 0 0',
                  fontSize: '.96rem',
                  lineHeight: 1.62,
                  opacity: 0.82,
                }}
              >
                {card.text}
              </p>
            </div>
          ))}
        </div>

        <div
          className="ba-hover-card ba-soft-hover"
          style={{
            marginTop: desktop ? '1.35rem' : '1.1rem',
            borderRadius: 26,
            padding: desktop ? '2rem' : '1.35rem',
            background: surfaceBgProminent,
            border: surfaceBorder,
            boxShadow: surfaceShadow,
            display: 'grid',
            gridTemplateColumns: desktop
              ? 'minmax(0,1.35fr) minmax(260px,.65fr)'
              : '1fr',
            columnGap: desktop ? '2.25rem' : undefined,
            rowGap: '1.25rem',
            alignItems: 'start',
          }}
        >
          <div
            style={{
              gridColumn: desktop ? '1 / -1' : undefined,
              textAlign: desktop ? 'left' : 'center',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.8rem',
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  flex: '0 0 44px',
                  borderRadius: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  background: iconBg,
                  border: iconBorder,
                  boxShadow: '0 8px 18px rgba(13,61,134,.1)',
                }}
              >
                <FeatureIcon name="plugins" />
              </div>
              <h3
                style={{
                  margin: 0,
                  fontSize: desktop ? '2rem' : '1.6rem',
                  letterSpacing: '-.035em',
                  lineHeight: 1.12,
                }}
              >
                {t.pluginLibraryTitle}
              </h3>
            </div>
          </div>

          <div
            style={{
              textAlign: desktop ? 'left' : 'center',
              maxWidth: 700,
              margin: desktop ? 0 : '0 auto',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: desktop ? '1.02rem' : '.96rem',
                lineHeight: 1.66,
                opacity: 0.84,
              }}
            >
              {t.pluginLibraryText}
            </p>
            <ul
              style={{
                margin: '.85rem 0 0',
                paddingLeft: desktop ? '1.2rem' : '1.1rem',
                textAlign: 'left',
                fontSize: desktop ? '1.02rem' : '.96rem',
                lineHeight: 1.66,
                opacity: 0.84,
              }}
            >
              {t.pluginLibraryBullets.map((bullet) => (
                <li
                  key={bullet}
                  style={{
                    marginTop: '.35rem',
                  }}
                >
                  {bullet}
                </li>
              ))}
            </ul>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '.65rem',
              justifyContent: desktop ? 'flex-end' : 'center',
              alignContent: 'center',
            }}
          >
            {t.plugins.map((plugin) => (
              <span
                key={plugin}
                style={{
                  padding: '.52rem .78rem',
                  borderRadius: 999,
                  background: labelBg,
                  border: labelBorder,
                  color: 'rgba(255,255,255,.9)',
                  fontSize: '.84rem',
                  fontWeight: 750,
                  lineHeight: 1,
                  boxShadow: '0 8px 18px rgba(13,61,134,.08)',
                }}
              >
                {plugin}
              </span>
            ))}
          </div>
        </div>
        </div>
        </div>

        <div
          style={{
            margin: desktop ? '2.75rem auto 1.85rem' : '2.1rem auto 1.45rem',
            maxWidth: 940,
            padding: desktop ? '3.05rem 0 2.95rem' : '2.55rem 0 2.45rem',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: desktop ? '.55rem' : '.45rem',
              bottom: desktop ? '.55rem' : '.45rem',
              left: '50%',
              width: 2,
              transform: 'translateX(-50%)',
              background:
                'linear-gradient(180deg,rgba(217,248,255,0),rgba(217,248,255,.18) 10%,rgba(217,248,255,.44) 32%,rgba(217,248,255,.44) 68%,rgba(217,248,255,.18) 90%,rgba(217,248,255,0))',
              boxShadow: '0 0 26px rgba(217,248,255,.18)',
              zIndex: 0,
            }}
          />
          <div
            className="ba-hover-card ba-soft-hover"
            style={{
              width: '100%',
              position: 'relative',
              zIndex: 1,
              display: 'grid',
              gridTemplateColumns: desktop ? 'auto minmax(0,1fr)' : '1fr',
              gap: desktop ? '1.35rem' : '1rem',
              alignItems: 'center',
              padding: desktop ? '1.35rem 1.6rem' : '1.3rem 1.15rem',
              borderRadius: 28,
              background:
                'linear-gradient(135deg,rgba(255,255,255,.18),rgba(217,248,255,.09) 48%,rgba(33,104,198,.08))',
              border: '1px solid rgba(224,247,255,.22)',
              boxShadow:
                '0 18px 48px rgba(13,61,134,.16), inset 0 1px 0 rgba(255,255,255,.08)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              textAlign: desktop ? 'left' : 'center',
            }}
          >
            <FeatureIcon
              name="ai"
              size={desktop ? 38 : 34}
              style={{
                color: '#fff',
                justifySelf: desktop ? 'start' : 'center',
                filter: 'drop-shadow(0 10px 18px rgba(13,61,134,.16))',
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: desktop ? '1.16rem' : '1rem',
                lineHeight: 1.68,
                opacity: 0.96,
                fontWeight: 850,
                color: 'rgba(255,255,255,.96)',
                textShadow: '0 10px 26px rgba(6,47,110,.14)',
              }}
            >
              {t.coreConceptsNote}
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: 0,
            borderRadius: 30,
            padding: 0,
            background: surfaceBgProminent,
            border: surfaceBorder,
            boxShadow: surfaceShadow,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              minHeight: desktop ? 46 : 42,
              padding: desktop ? '0 1.15rem' : '0 .9rem',
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateColumns: '72px minmax(0,1fr) 72px',
              alignItems: 'center',
              background:
                'linear-gradient(180deg,rgba(255,255,255,.18),rgba(255,255,255,.08))',
              borderBottom: '1px solid rgba(224,247,255,.15)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12)',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.45rem',
              }}
            >
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#ff5f57',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#ffbd2e',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: '#28c840',
                  boxShadow: '0 0 0 1px rgba(0,0,0,.12)',
                }}
              />
            </div>
            <div
              style={{
                minWidth: 0,
                textAlign: 'center',
                fontSize: desktop ? '.98rem' : '.9rem',
                fontWeight: 850,
                letterSpacing: '-.015em',
                color: 'rgba(255,255,255,.95)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: '0 2px 12px rgba(0,0,0,.16)',
              }}
            >
              {t.pluginEyebrow}
            </div>
            <div />
          </div>

          <div
            style={{
              padding: desktop ? '1.65rem 1.75rem 1.85rem' : '1rem .85rem 1.05rem',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                maxWidth: 920,
                textAlign: desktop ? 'left' : 'center',
                marginLeft: desktop ? 0 : 'auto',
                marginRight: desktop ? 0 : 'auto',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: desktop ? '2.35rem' : '1.8rem',
                  letterSpacing: '-.04em',
                  lineHeight: 1.08,
                }}
              >
                {t.pluginTitle}
              </h2>
              <FeatherUnderline align={desktop ? 'left' : 'center'} width={190} />
              <ol
                style={{
                  margin: '1.1rem 0 0',
                  paddingLeft: desktop ? '1.35rem' : '1.25rem',
                  fontSize: desktop ? '1.08rem' : '1rem',
                  lineHeight: 1.68,
                  opacity: 0.84,
                  maxWidth: 760,
                  textAlign: 'left',
                }}
              >
                {t.pluginSteps.map((step, stepIndex) => (
                  <li
                    key={step.text}
                    style={{
                      marginTop: stepIndex === 0 ? 0 : '.35rem',
                      paddingLeft: '.2rem',
                    }}
                  >
                    <span>{step.text}</span>
                    {step.subitems?.length ? (
                      <div
                        style={{
                          display: 'grid',
                          gap: '.28rem',
                          marginTop: '.45rem',
                          paddingLeft: desktop ? '.55rem' : '.25rem',
                        }}
                      >
                        {step.subitems.map((subitem, subIndex) => (
                          <div
                            key={subitem}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1.7rem minmax(0,1fr)',
                              gap: '.35rem',
                              alignItems: 'start',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 850,
                                color: 'rgba(217,248,255,.96)',
                              }}
                            >
                              {`${String.fromCharCode(97 + subIndex)})`}
                            </span>
                            <span>{subitem}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>

            <div
              aria-hidden="true"
              style={{
                height: 1,
                margin: desktop ? '2.35rem 0 1.55rem' : '1.75rem 0 1.15rem',
                background:
                  'linear-gradient(90deg,rgba(217,248,255,0),rgba(217,248,255,.38) 18%,rgba(255,255,255,.52) 50%,rgba(217,248,255,.38) 82%,rgba(217,248,255,0))',
                boxShadow: '0 0 24px rgba(217,248,255,.16)',
              }}
            />

            <div
              style={{
                fontSize: '.82rem',
                textTransform: 'uppercase',
                letterSpacing: '.12em',
                opacity: 0.68,
                fontWeight: 850,
                color: '#d9f8ff',
                textAlign: desktop ? 'left' : 'center',
              }}
            >
              {t.builderEyebrow}
            </div>
            <p
              style={{
                margin: '.75rem 0 0',
                fontSize: '.95rem',
                lineHeight: 1.58,
                opacity: 0.82,
                maxWidth: 820,
                textAlign: desktop ? 'left' : 'center',
                marginLeft: desktop ? 0 : 'auto',
                marginRight: desktop ? 0 : 'auto',
              }}
            >
              {t.visualSubtitle}
            </p>

                <div
                  style={{
                    position: 'relative',
                    marginTop: desktop ? '1.6rem' : '1.1rem',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: desktop ? 'repeat(3,minmax(0,1fr))' : '1fr',
                      columnGap: desktop ? '1.15rem' : 0,
                      rowGap: desktop ? 0 : '.85rem',
                      alignItems: 'stretch',
                    }}
                  >
                    <img
                      className="ba-hover-card ba-soft-hover"
                      src={PromptExample}
                      alt={`${t.promptLabel}: ${t.promptExample}`}
                      {...getImageOpenHandlers(
                        PromptExample,
                        `${t.promptLabel}: ${t.promptExample}`
                      )}
                      style={{
                        gridColumn: desktop ? '1 / -1' : undefined,
                        justifySelf: 'center',
                        width: '100%',
                        maxWidth: desktop ? 760 : '100%',
                        boxSizing: 'border-box',
                        display: 'block',
                        borderRadius: 22,
                        background: surfaceBg,
                        border: surfaceBorder,
                        boxShadow: surfaceShadowSoft,
                        cursor: 'zoom-in',
                      }}
                    />

                    {desktop ? (
                      <svg
                        viewBox="0 0 1000 110"
                        preserveAspectRatio="none"
                        style={{
                          gridColumn: '1 / -1',
                          width: '100%',
                          height: 92,
                          display: 'block',
                          overflow: 'visible',
                          pointerEvents: 'none',
                        }}
                      >
                        <defs>
                          <marker
                            id="builder-arrow"
                            markerWidth="10"
                            markerHeight="10"
                            refX="8.5"
                            refY="5"
                            orient="auto"
                          >
                            <path
                              d="M0 0L10 5L0 10Z"
                              fill="rgba(217,248,255,.86)"
                            />
                          </marker>
                        </defs>
                        <path
                          d="M500 0C500 42 170 46 170 108"
                          fill="none"
                          stroke="rgba(217,248,255,.66)"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeDasharray="6 9"
                          markerEnd="url(#builder-arrow)"
                          vectorEffect="non-scaling-stroke"
                        />
                        <path
                          d="M500 0C500 40 500 68 500 108"
                          fill="none"
                          stroke="rgba(217,248,255,.66)"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeDasharray="6 9"
                          markerEnd="url(#builder-arrow)"
                          vectorEffect="non-scaling-stroke"
                        />
                        <path
                          d="M500 0C500 42 830 46 830 108"
                          fill="none"
                          stroke="rgba(217,248,255,.66)"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeDasharray="6 9"
                          markerEnd="url(#builder-arrow)"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          color: '#d9f8ff',
                          fontSize: '1.45rem',
                          lineHeight: 1,
                          margin: '.1rem 0',
                          opacity: 0.9,
                        }}
                      >
                        ↓
                      </div>
                    )}

                    {t.promptNodes.map((node) => {
                      return (
                        <div
                          className="ba-hover-card ba-soft-hover"
                          key={node.title}
                          style={{
                            minHeight: desktop ? 180 : 'auto',
                            padding: desktop ? '1.1rem' : '1rem',
                            borderRadius: 18,
                            background:
                              surfaceBgSoft,
                            border: surfaceBorderSoft,
                            boxShadow: surfaceShadowSoft,
                            boxSizing: 'border-box',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '.8rem',
                            }}
                          >
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                flex: '0 0 38px',
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                background: iconBg,
                                border: iconBorder,
                              }}
                            >
                              <FeatureIcon name={node.icon} />
                            </div>
                            <div>
                              <div
                                style={{
                                  display: 'inline-flex',
                                  padding: '.22rem .45rem',
                                  borderRadius: 999,
                                  background: labelBg,
                                  border: labelBorder,
                                  color: '#d9f8ff',
                                  fontSize: '.68rem',
                                  fontWeight: 850,
                                  lineHeight: 1,
                                  marginBottom: '.42rem',
                                }}
                              >
                                {node.label}
                              </div>
                              <div
                                style={{
                                  fontSize: desktop ? '1.08rem' : '1.02rem',
                                  fontWeight: 850,
                                  lineHeight: 1.2,
                                  letterSpacing: '-.015em',
                                }}
                              >
                                {node.title}
                              </div>
                            </div>
                          </div>
                          <p
                            style={{
                              margin: '.85rem 0 0',
                              fontSize: '.84rem',
                              lineHeight: 1.52,
                              opacity: 0.8,
                            }}
                          >
                            {node.text}
                          </p>
                        </div>
                      )
                    })}
                </div>
              </div>
          </div>
        </div>

        <div
          style={{
            marginTop: desktop ? '4.6rem' : '3.3rem',
            marginBottom: desktop ? '4.4rem' : '3.2rem',
            marginLeft: desktop ? '-2rem' : '-1.15rem',
            marginRight: desktop ? '-2rem' : '-1.15rem',
          }}
        >
          <div
            style={{
              maxWidth: 840,
              margin: desktop ? '0 0 2rem' : '0 auto 1.6rem',
              textAlign: desktop ? 'left' : 'center',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: desktop ? '2.55rem' : '1.9rem',
                letterSpacing: '-.045em',
                lineHeight: 1.08,
              }}
            >
              {t.actionTitle}
            </h2>
            <FeatherUnderline align={desktop ? 'left' : 'center'} width={180} />
          </div>

          <div
            style={{
              display: 'grid',
              gap: 0,
              borderRadius: desktop ? 24 : 20,
              overflow: 'hidden',
              background: 'rgba(4,25,72,.28)',
              border: surfaceBorder,
              boxShadow: surfaceShadow,
            }}
          >
            <div
              style={{
                padding: desktop ? '.5rem .65rem' : '.45rem .5rem',
                minHeight: desktop ? 48 : 44,
                boxSizing: 'border-box',
                background:
                  'linear-gradient(180deg,rgba(8,38,92,.84),rgba(4,25,72,.92))',
                border: 0,
                borderBottom: '1px solid rgba(224,247,255,.16)',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,.1)',
                backdropFilter: 'blur(12px)',
                borderRadius: 0,
                display: 'grid',
                gridTemplateColumns: desktop
                  ? '34px minmax(0,1fr) 34px'
                  : '32px minmax(0,1fr) 32px',
                alignItems: 'center',
                gap: desktop ? '.85rem' : '.6rem',
              }}
            >
              <button
                type="button"
                aria-label={t.previousImageAria}
                onClick={goPrevSlide}
                style={{
                  width: desktop ? 34 : 32,
                  height: desktop ? 34 : 32,
                  borderRadius: 999,
                  border: '1px solid rgba(224,247,255,.2)',
                  background:
                    'linear-gradient(180deg,rgba(255,255,255,.18),rgba(255,255,255,.08))',
                  color: 'rgba(255,255,255,.94)',
                  fontSize: 0,
                  lineHeight: 1,
                  cursor: 'pointer',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  textShadow: '0 1px 8px rgba(0,0,0,.22)',
                  justifySelf: 'start',
                  transition: 'background .2s, border-color .2s, transform .2s',
                }}
              >
                <svg
                  aria-hidden="true"
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ display: 'block' }}
                >
                  <path
                    d="M15.5 5L8.5 12L15.5 19"
                    stroke="currentColor"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div
                style={{
                  maxWidth: 920,
                  margin: '0 auto',
                  color: 'rgba(255,255,255,.95)',
                  fontSize: desktop ? '.92rem' : '.8rem',
                  lineHeight: 1.35,
                  fontWeight: 750,
                  letterSpacing: '.005em',
                  textAlign: 'center',
                  textShadow: '0 2px 12px rgba(0,0,0,.18)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: desktop ? 'nowrap' : 'normal',
                }}
              >
                {activeActionCaption}
              </div>
              <button
                type="button"
                aria-label={t.nextImageAria}
                onClick={goNextSlide}
                style={{
                  width: desktop ? 34 : 32,
                  height: desktop ? 34 : 32,
                  borderRadius: 999,
                  border: '1px solid rgba(224,247,255,.2)',
                  background:
                    'linear-gradient(180deg,rgba(255,255,255,.18),rgba(255,255,255,.08))',
                  color: 'rgba(255,255,255,.94)',
                  fontSize: 0,
                  lineHeight: 1,
                  cursor: 'pointer',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  textShadow: '0 1px 8px rgba(0,0,0,.22)',
                  justifySelf: 'end',
                  transition: 'background .2s, border-color .2s, transform .2s',
                }}
              >
                <svg
                  aria-hidden="true"
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ display: 'block' }}
                >
                  <path
                    d="M8.5 5L15.5 12L8.5 19"
                    stroke="currentColor"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div
              style={{
                width: '100%',
                aspectRatio: desktop ? '16 / 8.6' : '16 / 11',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 0,
                background: 'rgba(4,25,72,.28)',
                border: 0,
                boxShadow: 'none',
                transform: 'translateZ(0)',
              }}
            >
              <div
                className="ba-carousel-track"
                onTransitionEnd={(event) => {
                  if (
                    event.target === event.currentTarget &&
                    event.propertyName === 'transform'
                  ) {
                    finishCarouselMove()
                  }
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  transform: `translate3d(-${carouselPosition * 100}%,0,0)`,
                  transition: carouselTransition
                    ? 'transform .38s cubic-bezier(.22,.7,.2,1)'
                    : 'none',
                }}
              >
                {carouselTrackSlides.map(({ slide, slideIndex }, trackIndex) => {
                  const isCurrentPosition = trackIndex === carouselPosition

                  return (
                    <div
                      key={`${slideIndex}-${trackIndex}`}
                      className="ba-carousel-slide"
                      aria-hidden={!isCurrentPosition}
                      style={{
                        flex: '0 0 100%',
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        background: 'rgba(4,25,72,.28)',
                        transform: 'translateZ(0)',
                      }}
                    >
                      <img
                        loading="eager"
                        decoding="async"
                        draggable={false}
                        src={getSlideImageSrc(slide.image)}
                        alt={isCurrentPosition ? t.actionImageAlt : ''}
                        aria-hidden={!isCurrentPosition}
                        {...(isCurrentPosition
                          ? getImageOpenHandlers(slide.image, t.actionImageAlt)
                          : {})}
                        style={{
                          width: '100%',
                          height: '100%',
                          maxWidth: '100%',
                          display: 'block',
                          objectFit: 'contain',
                          objectPosition: 'center center',
                          background: 'rgba(4,25,72,.28)',
                          flex: '0 0 auto',
                          borderRadius: 'inherit',
                          cursor: isCurrentPosition ? 'zoom-in' : 'default',
                          pointerEvents: isCurrentPosition ? 'auto' : 'none',
                          transform: 'translateZ(0)',
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            maxWidth: 840,
            marginTop: 0,
            marginBottom: '2.6rem',
            textAlign: desktop ? 'left' : 'center',
            marginLeft: desktop ? 0 : 'auto',
            marginRight: desktop ? 0 : 'auto',
          }}
        >
          <div
            style={{
              fontSize: '.82rem',
              textTransform: 'uppercase',
              letterSpacing: '.12em',
              opacity: 0.68,
              fontWeight: 850,
              marginBottom: '1rem',
              color: '#d9f8ff',
            }}
          >
            {t.featuresEyebrow}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: desktop ? '2.8rem' : '2rem',
              letterSpacing: '-.045em',
              lineHeight: 1.1,
            }}
          >
            {t.featuresTitle}
          </h2>
          <FeatherUnderline align={desktop ? 'left' : 'center'} width={210} />
          <p
            style={{
              margin: '1.2rem 0 0',
              fontSize: desktop ? '1.08rem' : '1rem',
              lineHeight: 1.65,
              opacity: 0.84,
            }}
          >
            {t.featuresText}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: desktop ? '1.3rem' : '1rem',
          }}
        >
          {featureCards.map((card, index) => {
            const imageFirst = desktop && index % 2 === 1
            const featureTileHeight = desktop ? 300 : 210
            const gridTemplateColumns = desktop
              ? imageFirst
                ? 'auto minmax(0,1fr)'
                : 'minmax(0,1fr) auto'
              : '1fr'

            const textPanel = (
              <div
                className="ba-hover-card"
                style={{
                  minHeight: desktop ? featureTileHeight : 'auto',
                  height: 'auto',
                  maxHeight: undefined,
                  minWidth: 0,
                  padding: desktop ? '1.85rem' : '1.35rem',
                  overflow: undefined,
                  borderRadius: 24,
                  background: surfaceBg,
                  border: surfaceBorder,
                  boxShadow: surfaceShadow,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1.1rem',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      flex: '0 0 44px',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      background: iconBg,
                      border: iconBorder,
                      boxShadow: '0 8px 18px rgba(13,61,134,.1)',
                    }}
                  >
                    <FeatureIcon name={card.icon} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: desktop ? '1.82rem' : '1.18rem',
                      letterSpacing: '-.02em',
                      lineHeight: 1.25,
                    }}
                  >
                    {card.title}
                  </h3>
                </div>
                <p
                  style={{
                    margin: '.75rem 0 0',
                    fontSize: desktop ? '1rem' : '.96rem',
                    lineHeight: 1.68,
                    opacity: 0.82,
                  }}
                >
                  {card.text}
                </p>
              </div>
            )
            const imagePanel = (
              <img
                className="ba-hover-card"
                src={getSlideImageSrc(card.image)}
                alt={`${card.title} ${t.featureIllustrationAlt}`}
                {...getImageOpenHandlers(
                  card.image,
                  `${card.title} ${t.featureIllustrationAlt}`
                )}
                style={{
                  height: featureTileHeight,
                  minHeight: featureTileHeight,
                  maxHeight: featureTileHeight,
                  width: desktop ? 'fit-content' : '100%',
                  maxWidth: '100%',
                  borderRadius: 24,
                  border: surfaceBorderSoft,
                  boxShadow: surfaceShadowSoft,
                  boxSizing: 'border-box',
                  display: 'block',
                  objectFit: 'contain',
                  objectPosition: 'center center',
                  background: 'rgba(4,25,72,.28)',
                  cursor: 'zoom-in',
                  justifySelf: desktop ? (imageFirst ? 'start' : 'end') : 'stretch',
                }}
              />
            )

            return (
              <div
                key={card.title}
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  height: undefined,
                  maxHeight: undefined,
                  gap: desktop ? '1.15rem' : '1rem',
                  alignItems: 'stretch',
                }}
              >
                {imageFirst ? (
                  <>
                    {imagePanel}
                    {textPanel}
                  </>
                ) : (
                  <>
                    {textPanel}
                    {imagePanel}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <section
        style={{
          maxWidth: $MAX_WIDTH,
          width: '100%',
          margin: '0 auto',
          padding: desktop ? '2rem 2rem 7.5rem' : '1.5rem 1.15rem 5rem',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            borderRadius: 30,
            padding: desktop ? '3.6rem' : '1.85rem',
            textAlign: 'center',
            background:
              surfaceBgProminent,
            border: surfaceBorder,
            boxShadow: surfaceShadow,
          }}
        >
          <div
            style={{
              fontSize: '.82rem',
              textTransform: 'uppercase',
              letterSpacing: '.12em',
              opacity: 0.7,
              fontWeight: 850,
              marginBottom: '1rem',
              color: '#d9f8ff',
            }}
          >
            {t.demoEyebrow}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: desktop ? '3rem' : '2rem',
              lineHeight: 1.08,
              letterSpacing: '-.05em',
            }}
          >
            {t.demoTitle}
          </h2>
          <FeatherUnderline align="center" width={230} />
          <p
            style={{
              margin: '1.25rem auto 0',
              maxWidth: 760,
              fontSize: desktop ? '1.13rem' : '1rem',
              lineHeight: 1.65,
              opacity: 0.84,
            }}
          >
            {t.demoText}
          </p>
          <button
            style={{
              width: desktop ? 'auto' : 'min(100%, 360px)',
              marginTop: '2.35rem',
              background: 'linear-gradient(90deg,#ffffff 0%,#f7fbff 100%)',
              color: '#08265c',
              padding: '1.05rem 2.45rem',
              fontSize: '1.04rem',
              fontWeight: 800,
              border: '1px solid rgba(255,255,255,.86)',
              borderRadius: 16,
              cursor: 'pointer',
              boxShadow: '0 14px 34px rgba(21,83,163,.2)',
            }}
            onClick={() => nav('/book-demo')}
          >
            {t.demoButton}
          </button>
        </div>
      </section>

      {modalImage && typeof document !== 'undefined' ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={modalImage.alt}
          onClick={() => setModalImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            padding: desktop ? '2.5rem' : '.75rem',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(2,12,35,.78)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            minHeight: '100vh',
            height: '100dvh',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 'min(96vw, 1500px)',
              maxHeight: desktop ? 'calc(100vh - 5rem)' : 'calc(100dvh - 1.5rem)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              aria-label={lang === 'zh' ? '关闭图片' : 'Close image'}
              onClick={() => setModalImage(null)}
              style={{
                position: 'fixed',
                top: desktop ? '1.25rem' : 'calc(env(safe-area-inset-top, 0px) + .75rem)',
                right: desktop ? '1.25rem' : 'calc(env(safe-area-inset-right, 0px) + .75rem)',
                zIndex: 1,
                width: desktop ? 44 : 38,
                height: desktop ? 44 : 38,
                borderRadius: 999,
                border: '1px solid rgba(224,247,255,.28)',
                background:
                  'linear-gradient(180deg,rgba(255,255,255,.22),rgba(255,255,255,.1))',
                color: '#fff',
                fontSize: desktop ? '1.7rem' : '1.45rem',
                lineHeight: 1,
                cursor: 'pointer',
                boxShadow:
                  '0 14px 34px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              ×
            </button>
            <img
              src={modalImage.src}
              alt={modalImage.alt}
              style={{
                maxWidth: desktop ? '100%' : 'calc(100vw - 1.5rem)',
                maxHeight: desktop ? 'calc(100vh - 5rem)' : 'calc(100dvh - 5rem)',
                width: 'auto',
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                borderRadius: desktop ? 22 : 16,
                boxShadow: '0 28px 90px rgba(0,0,0,.45)',
                background: 'rgba(4,25,72,.45)',
                border: '1px solid rgba(224,247,255,.22)',
              }}
            />
          </div>
        </div>
        , document.body
      ) : null}
    </div>
  )
}

export default HomePageClient
